import Icon, { LoadingOutlined } from '@ant-design/icons';
import { Alignment, Fit, Layout, useRive } from '@rive-app/react-webgl2';
import { Input, Modal, message } from 'antd';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import cookie from 'react-cookies';
import { useTranslation } from 'react-i18next';

// import LetMeRive from '@/assets/Letme_v1.riv';
import LetMeRive from '@/assets/Letme_v3.riv';
import CloseSvg from '@/components/Icon/svgs/close.svg';
import ReturnSVG from '@/components/Icon/svgs/return.svg';
import HandSvg from '@/components/Icon/svgs/waving_hand.svg';
import { sendFriendRequest } from '@/service/friend';
import type { IHomepageData } from '@/service/homePage';
import { EGlobalCookieKey } from '@/types/cookie';
import { innerHost } from '@/utils/host';
import { defaultHomeLogo } from '@/utils/operation';
import getTimezone from '@/utils/timezone';

import styles from './index.less';

export interface AddFriendModalProps {
  open: boolean;
  onClose: () => void;
  detail: IHomepageData | null;
  source?: string;
  circleName?: string;
  className?: string;
  handleConfirmCb?: () => void;
}

const AddFriendModal = ({
  open,
  onClose,
  detail,
  source,
  circleName,
  className,
  handleConfirmCb
}: AddFriendModalProps) => {
  const displayAvatar = detail?.avatar || defaultHomeLogo;
  const displayName = detail?.username || 'Second Me';
  // const displaySubtitle = '来自发现 · 💌约会对象 · 2共同好友';

  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState<string>('');
  const [takeoverLoading, setTakeoverLoading] = useState(false);
  const [historyValues, setHistoryValues] = useState<string[]>([]);
  const [tempValueIndex, setTempValueIndex] = useState(-1);
  const tempValueIndexRef = useRef(-1);

  // Unified push to history function
  // - Truncates history after current index (branching logic)
  // - Skips if value equals the item at current index
  const pushToHistory = (value: string) => {
    setHistoryValues((prev) => {
      const currentIndex = tempValueIndexRef.current;
      const currentValue = prev[currentIndex];

      // If value equals current position value, skip
      if (currentValue === value) {
        return prev;
      }

      // Truncate history after current index (discard future branch)
      const truncated = prev.slice(0, currentIndex + 1);
      const newHistory = [...truncated, value];
      const newIndex = newHistory.length - 1;

      tempValueIndexRef.current = newIndex;
      setTempValueIndex(newIndex);

      return newHistory;
    });
  };
  const abortControllerRef = useRef<AbortController | null>(null);
  // 打字机效果
  const pendingTextRef = useRef('');
  const typingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamEndedRef = useRef(false);
  const [typingLabel, setTypingLabel] = useState(t('friend.clickMe'));

  // Rive animation state: 0=idle, 1=working, 2=raise hand
  const [riveStatus, setRiveStatus] = useState<-1 | 0 | 1 | 2>(-1);
  // const riveReadyRef = useRef(false);

  // Rive animation setup
  const { RiveComponent, rive } = useRive(
    {
      src: LetMeRive,
      autoplay: true,
      artboard: 'wolai',
      stateMachines: 'state',
      layout: new Layout({
        fit: Fit.Cover,
        alignment: Alignment.Center
      })
    },
    {
      fitCanvasToArtboardHeight: true,
      useDevicePixelRatio: true,
      shouldResizeCanvasToContainer: true
    }
  );

  // // Fire coming animation on mount
  // useEffect(() => {
  //   if (rive && open && !riveReadyRef.current) {
  //     const inputs = rive.stateMachineInputs('state');
  //     const comingTrigger = inputs.find((input) => input.name === 'coming');

  //     if (comingTrigger) {
  //       comingTrigger.fire();
  //       riveReadyRef.current = true;
  //     }
  //   }

  //   if (!open) {
  //     riveReadyRef.current = false;
  //   }
  // }, [rive, open]);

  // Update rive status input when riveStatus changes
  useEffect(() => {
    if (rive) {
      const inputs = rive.stateMachineInputs('state');
      const statusInput = inputs.find((input) => input.name === 'status');

      if (statusInput) {
        statusInput.value = riveStatus;
      }
    }
  }, [rive, riveStatus]);

  // Update riveStatus based on typingLabel and takeoverLoading
  useEffect(() => {
    if (takeoverLoading) {
      // Working state when loading
      setRiveStatus(1);
    } else if (typingLabel.length > 0) {
      // Raise hand state when there's a label (bubble showing)
      setRiveStatus(2);
    } else {
      // Idle state
      setRiveStatus(0);
    }
  }, [takeoverLoading, typingLabel]);

  useEffect(() => {
    tempValueIndexRef.current = tempValueIndex;
  }, [tempValueIndex]);

  useEffect(() => {
    if (open && historyValues.length === 0) {
      // Push initial empty state and start generation
      pushToHistory('');
      handleTakeoverStream();
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      if (typingTimerRef.current) {
        clearInterval(typingTimerRef.current);
        typingTimerRef.current = null;
      }
    };
  }, []);

  // blur只在回退和我来的时候触发，不是统一的blur。否则在handleNextVersion会有问题
  const handleBlur = () => {
    const currentValue = historyValues[tempValueIndex];

    // If value unchanged, skip
    if (inputValue === currentValue) {
      return;
    }

    // Push edited value to history
    pushToHistory(inputValue);
  };

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (typingTimerRef.current) {
      clearInterval(typingTimerRef.current);
      typingTimerRef.current = null;
    }

    pendingTextRef.current = '';
    streamEndedRef.current = false;

    setTakeoverLoading(false);
    setTypingLabel('');

    // If no content generated, restore previous value
    if (!inputValue) {
      const currentValue = historyValues[tempValueIndexRef.current] || '';

      setInputValue(currentValue);

      return;
    }

    // Push generated content to history
    pushToHistory(inputValue);
  };

  const appendCharToOutput = (char: string) => {
    if (!char) return;

    // 副本生成放到handleStop和流结束时处理，这里只处理inputValue
    setInputValue((prev) => `${prev || ''}${char}`);
  };

  // 打字机
  const ensureTypingTimer = () => {
    if (typingTimerRef.current) return;

    typingTimerRef.current = setInterval(() => {
      if (!pendingTextRef.current) {
        if (streamEndedRef.current) {
          clearInterval(typingTimerRef.current!);
          typingTimerRef.current = null;
          streamEndedRef.current = false;
          setTakeoverLoading(false);
          setTypingLabel('');

          // Push generated content to history when stream ends
          setInputValue((currentInput) => {
            if (currentInput) {
              pushToHistory(currentInput);
            } else {
              // No content generated, restore previous value
              const currentValue = historyValues[tempValueIndexRef.current] || '';

              setInputValue(currentValue);
            }

            return currentInput;
          });
        }

        return;
      }

      const nextChar = pendingTextRef.current.charAt(0);

      pendingTextRef.current = pendingTextRef.current.slice(1);

      appendCharToOutput(nextChar);
    }, 30);
  };

  const handleSendRequest = () => {
    onClose();
    sendFriendRequest({
      targetUserId: detail?.userId || '',
      source,
      greeting: inputValue
    })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message);
        }

        handleConfirmCb?.();
        message.success(t('friend.connectModal.sendSuccess'));
      })
      .catch((err) => {
        message.error(err.message || t('error.oops'));
      });
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
  };

  const handlePrevVersion = () => {
    if (tempValueIndexRef.current <= 0) return;

    // Save current edit before navigating
    handleBlur();

    const prevIndex = tempValueIndexRef.current - 1;

    tempValueIndexRef.current = prevIndex;
    setTempValueIndex(prevIndex);
    setInputValue(historyValues[prevIndex] || '');
  };

  const handleNextVersion = () => {
    if (tempValueIndexRef.current >= historyValues.length - 1) return;

    const nextIndex = tempValueIndexRef.current + 1;

    setTempValueIndex(nextIndex);
    tempValueIndexRef.current = nextIndex;
    setInputValue(historyValues[nextIndex] || '');
  };

  const handleTakeoverStream = async () => {
    if (takeoverLoading) return;

    handleBlur();

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // const nextIndex = tempValueIndexRef.current + 1;

    if (typingTimerRef.current) {
      clearInterval(typingTimerRef.current);
      typingTimerRef.current = null;
    }

    pendingTextRef.current = '';
    streamEndedRef.current = false;

    // setHistoryValues((prev) => {
    //   const next = [...prev];

    //   next.splice(nextIndex, 0, '');

    //   return next;
    // });
    // setTempValueIndex(nextIndex);
    // tempValueIndexRef.current = nextIndex;
    setInputValue('');
    setTakeoverLoading(true);
    setTypingLabel(t('chatApp.taptap'));
    abortControllerRef.current = new AbortController();

    const token = cookie.load(EGlobalCookieKey.token);
    const baseUrl = innerHost();
    const timezone = getTimezone();

    try {
      const response = await fetch(`${baseUrl}/rest/os/takeover/execute/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          token: token,
          lang: cookie.load(EGlobalCookieKey.userLanguage) || 'en',
          ...(timezone ? { timeZone: timezone } : {})
        },
        body: JSON.stringify({
          scene: 'friendRequest',
          sceneId: detail?.userId,
          editText: inputValue || ''
        }),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No reader available');
      }

      let buffer = '';
      let isDone = false;

      while (!isDone) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();

        if (done) {
          isDone = true;
          streamEndedRef.current = true;
          ensureTypingTimer();

          break;
        }

        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');

        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim()) continue;

          if (line.startsWith('data:')) {
            const data = line.slice(5).trim();

            if (!data) {
              continue;
            }

            if (data === '[DONE]') {
              continue;
            }

            try {
              const parsedData = JSON.parse(data);
              const messageContent = parsedData.content;

              if (messageContent) {
                pendingTextRef.current += messageContent;
                ensureTypingTimer();
              }
            } catch (error) {
              message.error(t('error.oops'));
            }
          }
        }
      }

      abortControllerRef.current = null;
    } catch (error: unknown) {
      if (error instanceof Error && error.name === 'AbortError') {
        // Request aborted
      } else {
        console.error('Takeover SSE Error:', error);
      }

      abortControllerRef.current = null;
      setTakeoverLoading(false);
      setTypingLabel('');
      pendingTextRef.current = '';
      streamEndedRef.current = false;

      if (typingTimerRef.current) {
        clearInterval(typingTimerRef.current);
        typingTimerRef.current = null;
      }
    }
  };

  return (
    <Modal
      centered
      className={classNames(styles.addFriendModal, className)}
      closable={false}
      footer={null}
      maskClosable
      onCancel={onClose}
      open={open}
      width={400}
    >
      <div className={styles.modalCard}>
        <div className="flex flex-col w-full gap-[16px]">
          <button aria-label="Close" className={styles.closeButton} onClick={onClose} type="button">
            <Icon className={styles.closeIcon} component={CloseSvg} />
          </button>
          <div className={styles.header}>
            <img alt="" className={styles.avatar} src={displayAvatar} />
            <div className={styles.headerText}>
              <div className={styles.name}>{displayName}</div>
              <div className={styles.subtitle}>
                {(() => {
                  const isCircleSource = source === 'CIRCLE';
                  const validSourceSet = new Set([
                    'CONTACT',
                    'QR_CODE',
                    'NFC',
                    'DISCOVER',
                    'SEARCH',
                    'HOMEPAGE',
                    'INVITATION',
                    'CIRCLE',
                    'AI_SPARK',
                    'AI_SNAP',
                    'AI_MUSIC'
                  ]);
                  const sourceLabel =
                    source && validSourceSet.has(source) ? t(`friend.source.${source}`) : '';
                  const sourceText =
                    isCircleSource && circleName
                      ? t('friend.source.circleFrom', { name: circleName })
                      : sourceLabel
                        ? t('friend.source.from', { source: sourceLabel })
                        : '';

                  return `${sourceText ? `${sourceText} · ` : ''}${detail?.commonFriendNum} ${t(
                    'friend.commonFriends'
                  )}`;
                })()}
              </div>
            </div>
          </div>
          <div className={styles.divider} />
          <div className={styles.requestSection}>
            <div className={styles.requestHeader}>
              <div className={styles.miniAvatars}>
                <Icon
                  className={classNames(styles.returnIcon, {
                    [styles.returnIconDisabled]: tempValueIndex <= 0,
                    [styles.returnHidden]: historyValues.length < 2 || takeoverLoading
                  })}
                  component={ReturnSVG}
                  onClick={handlePrevVersion}
                />
                <Icon
                  className={classNames(styles.returnIcon, styles.returnIconReverse, {
                    [styles.returnHidden]:
                      historyValues.length <= 1 ||
                      takeoverLoading ||
                      tempValueIndex >= historyValues.length - 1
                  })}
                  component={ReturnSVG}
                  onClick={handleNextVersion}
                />
              </div>
            </div>
            <div
              className={classNames(styles.messageBox, {
                [styles.messageBoxLoading]: takeoverLoading
              })}
            >
              <div
                className={classNames(styles.takeoverTrigger, {
                  [styles.takeoverActive]: takeoverLoading
                })}
                onClick={handleTakeoverStream}
              >
                {typingLabel.length > 0 ? (
                  <div className={styles.takeoverBubble}>{typingLabel}</div>
                ) : null}
                <div className={styles.takeoverAvatar}>
                  <RiveComponent />
                </div>
              </div>
              {takeoverLoading ? <LoadingOutlined className={styles.messageIcon} /> : null}
              <div className={styles.messageContent}>
                <Input.TextArea
                  className={styles.messageTextArea}
                  disabled={takeoverLoading}
                  // onBlur={handleBlur}
                  onChange={(e) => handleInputChange(e.target.value)}
                  rows={4}
                  value={inputValue || ''}
                />
              </div>
              {takeoverLoading ? (
                <button className={styles.stopButton} onClick={handleStop} type="button">
                  <div className={styles.stopIcon} />
                </button>
              ) : null}
            </div>
          </div>
        </div>
        <button
          className={classNames(styles.addButton, {
            [styles.addButtonDisabled]: takeoverLoading
          })}
          disabled={takeoverLoading}
          onClick={() => handleSendRequest()}
          type="button"
        >
          <Icon className={styles.addIcon} component={HandSvg} />
          <span className={styles.addText}>{t('friend.addFriend')}</span>
        </button>
      </div>
    </Modal>
  );
};

export default AddFriendModal;
